/*
 * bb-motion.js — Beard & Brawn Motion Core (Tier 1)
 * Reveal engine + count-up. Native scroll only (no smooth-scroll / parallax /
 * scroll-scrub / scroll-progress chrome — owner rule: content stays put on
 * scroll). Vanilla, no build step. IIFE, defer-safe, idempotent, reduced-motion
 * safe. All engines are IntersectionObserver-based, so they work on native
 * scroll with no scroll-linked layout work.
 *
 * Shared motion contract (see bb-motion.css):
 *   <html>.bb-motion-on   added only when motion is allowed.
 *   [data-reveal]         "" | "fade" | "lines" | "wipe" | "stagger"  → .is-in on enter
 *   [data-reveal-delay]   ms → --rv-delay
 *   [data-count-to]       N (+ optional data-count-suffix)  → 0→N on scroll-in
 *                         (fires whether or not nested under a [data-reveal])
 */
(function () {
  'use strict';

  // ---- Idempotency guard ------------------------------------------------
  if (window.__bbMotionInit) return;
  window.__bbMotionInit = true;

  // Per-element guards so re-scanning a scope (initial load, then a Shopify
  // editor section re-render) is idempotent: an element already wired is never
  // observed or animated twice. Sets survive across initScope() calls.
  var seenReveal = new WeakSet();
  var seenCount = new WeakSet();

  // Registry of scoped observers keyed by their section root, so a
  // shopify:section:unload can disconnect exactly what a section registered
  // (no stale IO/RO references accumulate across editor re-renders).
  var scopeObservers = []; // [{ root, observer }]
  function registerObserver(root, observer) {
    scopeObservers.push({ root: root, observer: observer });
    return observer;
  }
  function teardownScope(root) {
    for (var i = scopeObservers.length - 1; i >= 0; i--) {
      if (root === document || root.contains(scopeObservers[i].root) || scopeObservers[i].root === root) {
        try { scopeObservers[i].observer.disconnect(); } catch (e) {}
        scopeObservers.splice(i, 1);
      }
    }
  }

  var REDUCE = false;
  try {
    REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    REDUCE = false;
  }

  var root = document.documentElement;

  // Shopify CLI `theme dev` (localhost hot reload) swaps section DOM without
  // firing the editor's shopify:section:load events, so freshly swapped nodes
  // would otherwise sit in their hidden [data-reveal] pre-state forever.
  // Detected once; the dev-only MutationObservers below re-scan on DOM swaps.
  // Zero cost on the real storefront.
  var IS_LOCAL_DEV = /^(127\.0\.0\.1|localhost)$/.test(location.hostname);

  // ---- Helpers ----------------------------------------------------------
  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }

  // Run cb once fonts have settled (so text measurement uses real metrics, not
  // the swap fallback). Defensive timeout in case fonts.ready never resolves.
  function whenFontsReady(cb) {
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      var done = false;
      var go = function () { if (!done) { done = true; cb(); } };
      document.fonts.ready.then(go);
      setTimeout(go, 1200);
    } else {
      cb();
    }
  }

  // ---- Count-up (shared by both branches) -------------------------------
  function setCountFinal(el) {
    var to = parseFloat(el.getAttribute('data-count-to'));
    if (isNaN(to)) return;
    var suffix = el.getAttribute('data-count-suffix') || '';
    el.textContent = formatNumber(to) + suffix;
  }

  function formatNumber(n) {
    // Integers stay clean; preserve the magnitude formatting (no decimals for whole numbers).
    if (Math.abs(n - Math.round(n)) < 0.001) return String(Math.round(n));
    return String(Math.round(n * 10) / 10);
  }

  function animateCount(el) {
    // Idempotent: a counter can be reached both by its reveal parent AND by the
    // dedicated count observer below. Whichever fires first wins; the other no-ops.
    if (el.__bbCounted) return;
    el.__bbCounted = true;
    var to = parseFloat(el.getAttribute('data-count-to'));
    if (isNaN(to)) { setCountFinal(el); return; }
    var suffix = el.getAttribute('data-count-suffix') || '';
    var dur = 1400;
    var start = null;

    function easeOut(t) {
      return 1 - Math.pow(1 - t, 3);
    }

    function step(ts) {
      if (start === null) start = ts;
      var p = clamp((ts - start) / dur, 0, 1);
      var val = to * easeOut(p);
      el.textContent = formatNumber(val) + suffix;
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = formatNumber(to) + suffix;
    }
    requestAnimationFrame(step);
  }

  // =======================================================================
  // REDUCED MOTION: render everything in final state, init nothing.
  // =======================================================================
  if (REDUCE) {
    var showAll = function (scope) {
      var counts = scope.querySelectorAll('[data-count-to]');
      for (var i = 0; i < counts.length; i++) setCountFinal(counts[i]);
      var reveals = scope.querySelectorAll('[data-reveal]');
      for (var j = 0; j < reveals.length; j++) reveals[j].classList.add('is-in');
    };
    ready(function () {
      showAll(document);
      root.classList.add('bb-motion-ready'); // clears the CSS failsafe (see bb-motion.css)
      // Dev hot reload: re-show swapped-in nodes (see IS_LOCAL_DEV above).
      if (IS_LOCAL_DEV && 'MutationObserver' in window) {
        var devShowTimer = null;
        new MutationObserver(function () {
          clearTimeout(devShowTimer);
          devShowTimer = setTimeout(function () { showAll(document); }, 150);
        }).observe(document.body, { childList: true, subtree: true });
      }
    });
    // Editor: a re-rendered section brings fresh hidden nodes — show them too.
    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:load', function (e) {
        if (e.target && e.target.querySelectorAll) showAll(e.target);
      });
    }
    return;
  }

  // =======================================================================
  // FULL MOTION
  // =======================================================================
  root.classList.add('bb-motion-on');

  ready(function () {
    // Per-scope engines over the whole document on first paint.
    initScope(document);
    // Signal success → the CSS failsafe in bb-motion.css stops forcing reveals
    // visible (html.bb-motion-on:not(.bb-motion-ready) is its scope). If this
    // line is never reached (script error / CDN failure), the failsafe fires.
    root.classList.add('bb-motion-ready');
  });

  // Scoped (re-)initialisation. `root` is the whole document on first load, or a
  // single section root on a Shopify editor shopify:section:load. Idempotent via
  // the per-element WeakSet guards, so overlapping scans never double-wire.
  // Every engine is IntersectionObserver-based → native scroll, no scroll-linked
  // work and nothing to tear down on pagehide/bfcache beyond the observers, which
  // the editor-lifecycle path already disconnects.
  function initScope(root) {
    initReveal(root);
    initCounts(root);
  }

  // ---- Shopify theme-editor lifecycle -----------------------------------
  // Only wired in design mode → zero storefront cost. On section load, scan the
  // fresh section root (its [data-reveal]/counters were re-rendered hidden under
  // html.bb-motion-on and would otherwise stay hidden forever). On unload,
  // disconnect any observers registered for nodes inside that section.
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target && e.target.querySelectorAll) initScope(e.target);
    });
    document.addEventListener('shopify:section:unload', function (e) {
      if (e.target) teardownScope(e.target);
    });
  }

  // Dev hot reload: re-wire swapped-in nodes (see IS_LOCAL_DEV above).
  // initScope is idempotent via the per-element WeakSets, so a debounced
  // whole-document re-scan is safe and only touches genuinely new nodes.
  if (IS_LOCAL_DEV && 'MutationObserver' in window) {
    ready(function () {
      var devScanTimer = null;
      new MutationObserver(function () {
        clearTimeout(devScanTimer);
        devScanTimer = setTimeout(function () { initScope(document); }, 150);
      }).observe(document.body, { childList: true, subtree: true });
    });
  }

  // ---- Count-up engine (independent of [data-reveal]) -------------------
  // Guarantees every [data-count-to] fires on first scroll-into-view, whether or
  // not a section agent nested it under a [data-reveal]. animateCount() is
  // idempotent (__bbCounted), so reaching a counter via both paths is harmless.
  function initCounts(scope) {
    var all = scope.querySelectorAll('[data-count-to]');
    var els = [];
    for (var g = 0; g < all.length; g++) {
      if (seenCount.has(all[g])) continue; // idempotent across re-scans
      seenCount.add(all[g]);
      els.push(all[g]);
    }
    if (!els.length) return;

    // Accessibility + correctness (Task 4): the visible pre-state is seeded to
    // "0" for the ticker, but assistive tech must always read the TRUE final
    // value, and any counter that never intersects (hidden content) must not be
    // left stuck at "0". So: expose the final value via aria-label immediately,
    // and arm a timeout that writes the real value if it hasn't animated in time.
    function seedCounter(el) {
      var to = parseFloat(el.getAttribute('data-count-to'));
      var suffix = el.getAttribute('data-count-suffix') || '';
      if (!isNaN(to)) el.setAttribute('aria-label', formatNumber(to) + suffix);
      el.textContent = '0' + suffix;
    }

    if (!('IntersectionObserver' in window)) {
      // No IO: write the final value on first paint (nothing to trigger on).
      for (var n = 0; n < els.length; n++) { seedCounter(els[n]); animateCount(els[n]); }
      return;
    }
    var cio = registerObserver(scope, new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            animateCount(entries[i].target);
            cio.unobserve(entries[i].target);
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
    ));
    for (var j = 0; j < els.length; j++) {
      seedCounter(els[j]);
      cio.observe(els[j]);
      // Fallback: if a counter never scrolls into view (e.g. inside an accordion
      // or a tab that's never opened), snap it to its final value after a few
      // seconds so it never displays a misleading "0". animateCount() is
      // idempotent, so the normal in-view path still wins if it fires first.
      (function (node) {
        setTimeout(function () {
          if (!node.__bbCounted) { try { cio.unobserve(node); } catch (e) {} setCountFinal(node); node.__bbCounted = true; }
        }, 4000);
      })(els[j]);
    }
  }

  // ---- Reveal engine ----------------------------------------------------
  function initReveal(scope) {
    var all = scope.querySelectorAll('[data-reveal]');
    var els = [];
    for (var g = 0; g < all.length; g++) {
      if (seenReveal.has(all[g])) continue; // idempotent across re-scans
      seenReveal.add(all[g]);
      els.push(all[g]);
    }
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      // No IO support: show everything immediately.
      for (var k = 0; k < els.length; k++) reveal(els[k]);
      return;
    }

    var io = registerObserver(scope, new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            var t = entries[i].target;
            // Lines-mode: on a cold cache the split (whenFontsReady → prepLines)
            // can still be pending. Revealing now would set .is-in on spans that
            // are then rebuilt already-final (no animation). Defer: mark that the
            // element wants to reveal, and let prepLines fire it once split. If
            // the split has already finished (__bbLinesReady), reveal immediately.
            if (t.getAttribute('data-reveal') === 'lines' && !t.__bbLinesReady) {
              t.__bbWantReveal = true;
            } else {
              reveal(t);
            }
            io.unobserve(t);
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.01 }
    ));

    for (var j = 0; j < els.length; j++) {
      var el = els[j];
      var delay = el.getAttribute('data-reveal-delay');
      if (delay) el.style.setProperty('--rv-delay', parseInt(delay, 10) + 'ms');

      var mode = el.getAttribute('data-reveal');
      if (mode === 'lines') {
        // Split AFTER fonts settle so line-grouping (offsetTop) measures the real
        // wrap, not swap-fallback metrics. When the split completes we flip
        // __bbLinesReady and, if the element was already scrolled into view
        // (io already unobserved it with __bbWantReveal), reveal it right then —
        // so the freshly-built spans animate rather than being born final.
        (function (node) {
          whenFontsReady(function () {
            prepLines(node);
            node.__bbLinesReady = true;
            if (node.__bbWantReveal) reveal(node);
          });
        })(el);
      }
      if (mode === 'stagger') prepStagger(el);

      io.observe(el);
    }
  }

  function reveal(el) {
    // will-change is applied JIT — only for the element about to animate — then
    // released once the transition ends, so a long page never holds dozens of
    // simultaneous compositor hints from first paint (the CSS pre-states no
    // longer carry will-change). Harmless if the property never transitions
    // (transitionend just never fires; the hint self-clears on the fallback).
    hintWillChange(el);
    el.classList.add('is-in');
    if (el.hasAttribute('data-count-to')) animateCount(el);
    var counts = el.querySelectorAll ? el.querySelectorAll('[data-count-to]') : [];
    for (var i = 0; i < counts.length; i++) animateCount(counts[i]);
  }

  // Apply a mode-appropriate will-change to the node(s) that actually animate,
  // and clear it when their transition finishes. For lines/stagger the moving
  // parts are descendants (.bb-line-i / direct children), so hint those.
  function hintWillChange(el) {
    var mode = el.getAttribute('data-reveal');
    var targets;
    if (mode === 'lines') {
      targets = el.querySelectorAll('.bb-line-i');
    } else if (mode === 'stagger') {
      targets = el.children;
    } else {
      targets = [el]; // "", "fade", "wipe"
    }
    for (var i = 0; i < targets.length; i++) {
      (function (node) {
        if (!node || !node.style) return;
        node.style.willChange = mode === 'wipe' ? 'clip-path' : 'transform, opacity';
        var clear = function () {
          node.style.willChange = 'auto';
          node.removeEventListener('transitionend', clear);
        };
        node.addEventListener('transitionend', clear);
        // Fallback in case transitionend never fires (interrupted / no transition).
        setTimeout(clear, 1600);
      })(targets[i]);
    }
  }

  // Split text into line-masked rising lines.
  function prepLines(el) {
    // Only split plain-text-ish headings; skip if it has block children.
    // el.textContent would otherwise flatten nested markup (e.g. an
    // inline-richtext heading with <p>/<strong>) into a single run of words,
    // destroying the original structure. Leave such nodes untouched (they stay
    // visible via the reveal-host's own state); the reveal still fires normally.
    if (el.children.length > 0) return;
    // Fold whitespace EXCEPT NBSP (\u00A0): authors use it to pin word pairs
    // together, and the word-splitting below must keep those pairs as one
    // unbreakable chunk or the rebuilt heading re-wraps differently.
    var text = el.textContent.replace(/[^\S\u00A0]+/g, ' ').trim();
    if (!text) return;

    var words = text.split(' ');
    el.textContent = '';
    var wordSpans = [];
    for (var i = 0; i < words.length; i++) {
      var w = document.createElement('span');
      w.className = 'bb-word';
      w.style.display = 'inline-block';
      w.textContent = words[i];
      el.appendChild(w);
      wordSpans.push(w);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
    }

    // Group words by offsetTop into lines.
    var lines = [];
    var current = null;
    var lastTop = null;
    for (var j = 0; j < wordSpans.length; j++) {
      var top = wordSpans[j].offsetTop;
      if (lastTop === null || Math.abs(top - lastTop) > 2) {
        current = [];
        lines.push(current);
        lastTop = top;
      }
      current.push(wordSpans[j]);
    }

    // Rebuild as line-masked structure.
    el.textContent = '';
    for (var l = 0; l < lines.length; l++) {
      var outer = document.createElement('span');
      outer.className = 'bb-line';
      var inner = document.createElement('span');
      inner.className = 'bb-line-i';
      inner.style.setProperty('--bb-line-index', l);
      inner.textContent = lines[l]
        .map(function (s) {
          return s.textContent;
        })
        .join(' ');
      outer.appendChild(inner);
      el.appendChild(outer);
    }
  }

  function prepStagger(el) {
    var kids = el.children;
    for (var i = 0; i < kids.length; i++) {
      kids[i].style.setProperty('--rv-i', i);
    }
  }

})();
